import { Helmet } from "react-helmet-async";
import { siteConfig } from "../lib/siteConfig";

/**
 * Seo — per-page <head> via react-helmet-async. Prerendered to static HTML by
 * react-snap so non-JS crawlers (Google, ChatGPT, Perplexity, Gemini) see it.
 *
 * Props: title, description, path (for canonical), image, type, noindex,
 * keywords (array), publishedTime, modifiedTime, jsonLd (object | array).
 * Everything falls back to siteConfig, so <Seo /> with no props is valid.
 */
export default function Seo({
  title,
  description = siteConfig.defaultDescription,
  path = "",
  image = siteConfig.defaultOgImage,
  type = "website",
  noindex = false,
  keywords,
  publishedTime,
  modifiedTime,
  jsonLd,
}) {
  const fullTitle = title
    ? siteConfig.titleTemplate.replace("%s", title)
    : `${siteConfig.siteName} — ${siteConfig.tagline}`;
  const canonical = `${siteConfig.baseUrl}${path}`;
  const ogImage = image?.startsWith("http") ? image : `${siteConfig.baseUrl}${image}`;
  const blocks = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords?.length ? <meta name="keywords" content={keywords.join(", ")} /> : null}
      {noindex ? <meta name="robots" content="noindex,nofollow" /> : null}
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:site_name" content={siteConfig.siteName} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={siteConfig.locale} />
      {publishedTime ? <meta property="article:published_time" content={publishedTime} /> : null}
      {modifiedTime ? <meta property="article:modified_time" content={modifiedTime} /> : null}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={siteConfig.twitterHandle} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}
